// useScanWedge — the generic keyboard-wedge listener (extracted from
// useBadgeWedge for Phase 2 wave 2e; the badge hook is now a thin wrapper).
//
// A hardware imager in keyboard-wedge mode types the code as keystrokes into
// whatever has focus, then usually (not always) sends Enter. This hook
// listens on WINDOW, in the capture phase, so the scan works no matter what is
// focused and is handled before any field or React handler sees the keys. It
// stands down while the operator is typing into a field of their own (see
// `captureInFields` for the opt-in exception).
//
// The accept test is the payload SHAPE, not timing: callers pass a `match`
// that inspects the keystroke buffer and returns the canonical payload when
// its tail is a well-formed scan. A human can't produce a well-formed payload
// by accident, so slow scans are still recognised and stray typing still
// isn't — and the hook works identically with and without a trailing Enter.

import { useEffect, useRef } from 'react'
import { insideWindow, type ArmedAt } from './useScannerEnterGuard'

export interface ScanWedgeOptions {
  enabled?:     boolean
  /** Keep this much buffer tail; a little above the longest payload. */
  bufferMax?:   number
  /** Gap after which the buffer is stale (a wedge is ~10-30 ms/char). */
  idleResetMs?: number
  /**
   * A second matcher, tried on the terminating Enter against the WHOLE buffer.
   *
   * For codes with a fixed shape (a badge is BW1 + 12) the per-keystroke
   * `match` is right and does not need a terminator — which matters, because
   * not every reader sends one. For a code with no fixed LENGTH (an RFID UID is
   * 4-32 hex characters) it is wrong: the per-keystroke test fires on the first
   * prefix that satisfies it, and '04A3B2C1' is delivered as '04A3'. Those
   * matchers go here.
   *
   * `match` still wins when both could fire, because it fires first.
   */
  matchOnFlush?: (buffer: string) => string | null
  /**
   * Also take a scan typed into a FOCUSED input, textarea or select when it
   * arrives as a burst (every key within `burstGapMs`) and matches. The field
   * is put back exactly as it was before the burst — text the operator had
   * typed stays — and the scan fires.
   *
   * Opt in on a page where a field can hold focus while the operator scans the
   * NEXT thing: a dialog that focused its first field, a filter box. Without
   * it Precool's pulp-out field took the next dolly's barcode as a temperature
   * and saved it, and Security's checkpoint sheet recorded nothing (packhouse
   * audit PM-INB-19/20, PM-ADM-02 · PW-01). Leave it OFF where a field exists
   * to RECEIVE the scan (Tipping's "Scan or type bin #") — it would take the
   * scan away from that field.
   *
   * The field may already hold text: the audit's case was a pulp-out of "1.1"
   * that became "1.1202600418" and was saved. Not taken: a contentEditable, and
   * anything slower than a burst — a human cannot type a well-formed payload at
   * wedge speed, and the shape test still applies on top.
   *
   * ⚠️ A select on an opted-in page loses keyboard type-ahead: its printable
   * keys are held for the burst, because type-ahead would change the select —
   * and fire its onChange — on the scan's FIRST character.
   */
  captureInFields?: boolean
  /** Max gap between two keys of an in-field burst. A wedge types ~10-30 ms/char. */
  burstGapMs?: number
  /**
   * Keep listening while focus sits on an input that takes no TEXT (a checkbox,
   * a radio, an input button). Off by default: the hook then stands down for
   * EVERY input, as it always has.
   *
   * Opt in only on a screen that also cancels the reader's trailing Enter
   * (`useScannerEnterGuard`). Without that guard, the scan fires AND its Enter
   * still lands on the focused box, toggling it or submitting the form (Chrome
   * submits from a focused checkbox). Tag pallets (F10 UF-04) opts in: a row
   * ticked by hand keeps focus on its box, and the scanner must not go deaf
   * from the first hand tick.
   */
  nonTextInputsAreNotTyping?: boolean
}

/** How long after a matched scan its trailing Enter is still the scanner's. Measured
 *  the way useScannerEnterGuard measures: dispatch time AND creation time, because
 *  the scan's own re-render can outlast the window on a Chrome 86 handset. */
const ENTER_SWALLOW_MS = 150

/**
 * Inputs that take no TEXT. A focused checkbox is the common one: tick a row by
 * hand and focus stays on its box, so a hook that stands down for every INPUT
 * goes deaf to the scanner from the first hand tick. Only a caller that opts in
 * (`nonTextInputsAreNotTyping`, Tag pallets F10 UF-04) treats these as not
 * typing.
 */
const NON_TEXT_INPUT: ReadonlySet<string> = new Set([
  'checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image',
])

/**
 * True when the keystroke is going into something the operator is typing in.
 * By default every INPUT counts; with `nonTextInputsAreNotTyping` an input that
 * takes no text (checkbox, radio, input button) does not.
 */
export function isTypingTarget(target: EventTarget | null, nonTextInputsAreNotTyping = false): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT') {
    return !(nonTextInputsAreNotTyping && NON_TEXT_INPUT.has((target as HTMLInputElement).type))
  }
  return tag === 'TEXTAREA' || tag === 'SELECT' || !!target.isContentEditable
}

type FieldEl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

/** Put a field back to `value` the way React will notice: the NATIVE setter
 *  plus an input event, so a controlled field's onChange sees it too. */
function restoreField(el: FieldEl, value: string): void {
  if (el instanceof HTMLSelectElement) { el.value = value; return }
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

/** Cancel an event before any field, form or React handler sees it. */
function kill(e: KeyboardEvent): void {
  e.preventDefault()
  e.stopImmediatePropagation()
}

export function useScanWedge(
  match: (buffer: string) => string | null,
  onScan: (payload: string) => void,
  opts: ScanWedgeOptions = {},
): void {
  const { enabled = true, bufferMax = 64, idleResetMs = 500, matchOnFlush, captureInFields = false, burstGapMs = 35, nonTextInputsAreNotTyping = false } = opts
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan
  const matchRef = useRef(match)
  matchRef.current = match
  const flushRef = useRef(matchOnFlush)
  flushRef.current = matchOnFlush

  useEffect(() => {
    if (!enabled) return

    let buffer = ''
    let lastKeyAt = 0
    // In-field burst (captureInFields): the field, what it held before the
    // burst, and the keys so far.
    let field: { el: FieldEl; start: string; buf: string } | null = null
    let lastFieldKeyAt = 0
    // After a match, the scanner's own trailing Enter is on its way. By then
    // onScan has often opened a dialog that focused its first field, so that
    // Enter would SUBMIT the dialog — Precool started cooling with no target
    // temperature before the operator ever saw it (PM-INB-18 · PW-01). The
    // listener runs in the CAPTURE phase on window, so it wins against the
    // field's own onKeyDown and the form's implicit submit.
    let enterArmedAt: ArmedAt | null = null

    const fire = (payload: string) => {
      enterArmedAt = { wall: Date.now(), perf: performance.now() }
      onScanRef.current(payload)
    }

    // fallow-ignore-next-line complexity
    const inField = (e: KeyboardEvent, el: FieldEl) => {
      const now = Date.now()
      const continuing = field !== null && field.el === el && now - lastFieldKeyAt <= burstGapMs
      lastFieldKeyAt = now
      const isSelect = el instanceof HTMLSelectElement
      if (e.key === 'Enter') {
        if (!continuing || !field) { field = null; return }
        const payload = flushRef.current?.(field.buf) ?? matchRef.current(field.buf)
        const f = field
        field = null
        if (payload == null) return
        kill(e)
        restoreField(f.el, f.start)
        fire(payload)
        return
      }
      if (e.key.length !== 1) { field = null; return }
      // A new burst remembers what the field held, so a match can put it back.
      if (!continuing) field = { el, start: el.value, buf: '' }
      const f = field as NonNullable<typeof field>
      f.buf = (f.buf + e.key).slice(-bufferMax)
      if (isSelect) e.preventDefault()          // no type-ahead — see captureInFields
      const payload = matchRef.current(f.buf)
      if (payload == null) return
      field = null
      kill(e)                                   // the last character never lands
      restoreField(f.el, f.start)
      fire(payload)
    }

    // The scanner's trailing Enter, if it is due: cancelled before anything sees it.
    const swallowedEnter = (e: KeyboardEvent): boolean => {
      if (!enterArmedAt) return false
      const due = insideWindow(enterArmedAt, e.timeStamp, ENTER_SWALLOW_MS)
      enterArmedAt = null
      if (!due || e.key !== 'Enter') return false
      kill(e)
      return true
    }

    // A key that is NOT going into a field: the document-level scan buffer.
    const freeKey = (e: KeyboardEvent) => {
      const now = Date.now()
      if (now - lastKeyAt > idleResetMs) buffer = ''
      lastKeyAt = now

      // Enter terminates a scan on most wedge configurations; treat it as a
      // flush, but don't REQUIRE it.
      if (e.key === 'Enter') {
        // A code whose shape has no fixed LENGTH cannot be matched per
        // keystroke — the test would fire on the first prefix that satisfies it
        // and truncate every scan. Those matchers run HERE instead, on the
        // terminator the reader sends. Opt-in: without matchOnFlush this is
        // exactly the flush-and-forget it always was.
        const whole = buffer
        buffer = ''
        const payload = whole && flushRef.current ? flushRef.current(whole) : null
        if (payload == null) return
        e.preventDefault()
        fire(payload)
        return
      }
      if (e.key.length !== 1) return          // modifiers, arrows, etc.

      buffer = (buffer + e.key).slice(-bufferMax)

      const payload = matchRef.current(buffer)
      if (payload == null) return

      buffer = ''
      // Cancel the character that completed the scan. The scanner's trailing
      // Enter is a SEPARATE event, handled by swallowedEnter — this line never
      // stopped it (it used to claim it did).
      e.preventDefault()
      fire(payload)
    }

    const handler = (e: KeyboardEvent) => {
      if (swallowedEnter(e)) return
      // Never swallow the operator's own typing — unless the page opted in to
      // taking a scan-speed burst out of a focused field.
      if (!isTypingTarget(e.target, nonTextInputsAreNotTyping)) { freeKey(e); return }
      const el = e.target as HTMLElement
      if (captureInFields && !el.isContentEditable) inField(e, el as FieldEl)
    }

    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [enabled, bufferMax, idleResetMs, captureInFields, burstGapMs, nonTextInputsAreNotTyping])
}
